<template>
  <div
    class="d-flex flex-column gap-4"
    :class="{
      'advance-reporting-loading': isLoading
    }"
  >
    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-start flex-wrap">
      <h4>{{ pageName }}</h4>
    </div>
    <!-- HEADER -->

    <!-- SERVER ERROR -->
    <ErrorHandler
      v-if="serverError != null"
      :error="serverError"
      @done="serverError = null"
    />
    <!-- SERVER ERROR -->

    <!-- FILTERS -->
    <CRow class="gap-4">
      <!-- SEARCH SCHEDULED REPORTS -->
      <CCol md class="d-flex flex-column gap-2">
        <InputField
          prepend-icon="ri-search-line"
          id="search"
          placeholder="Search scheduled reports..."
          v-model="search"
        />
      </CCol>
      <!-- SEARCH SCHEDULED REPORTS -->

      <!-- PAGE SELECTOR -->
      <CCol md class="d-flex flex-column gap-2 justify-content-end">
        <Perpage
          use-first-option-as-default
          use-dynamic-update
          showing-entries
          :options="pageOptions"
          :pagination="paginationWithExtra"
          @on-page-change="setPage"
        />
      </CCol>
      <!-- PAGE SELECTOR -->
    </CRow>
    <!-- FILTERS -->

    <!-- TABLE -->
    <DataTable :fields="fields" :items="list">
      <template #school_name="{ item }">
        <td>
          <div class="d-flex align-items-center gap-1">
            <span>{{ getFormattedSchools(item).firstPart }}</span>
            <span
              v-if="getFormattedSchools(item).hasMultiple"
              class="clickable-plus-text"
              @click="
                openNamesPopup(
                  'Schools',
                  getFormattedSchools(item).allNames,
                  item
                )
              "
            >
              {{ getFormattedSchools(item).plusPart }}
            </span>
          </div>
        </td>
      </template>
      <template #format="{ item }">
        <td>
          {{ String(item.format).toUpperCase() || "" }}
        </td>
      </template>
      <template #data_points="{ item }">
        <td>
          <div class="d-flex align-items-center gap-1">
            <span>{{ getFormattedDataPoints(item).firstPart }}</span>
            <span
              v-if="getFormattedDataPoints(item).hasMultiple"
              class="clickable-plus-text"
              @click="
                openNamesPopup(
                  'Data Points',
                  getFormattedDataPoints(item).allNames,
                  item
                )
              "
            >
              {{ getFormattedDataPoints(item).plusPart }}
            </span>
          </div>
        </td>
      </template>

      <template #start_date="{ item }">
        <td>
          {{ formatDate(item.start_date) }}
        </td>
      </template>

      <template #end_date="{ item }">
        <td>
          {{ formatDate(item.end_date) }}
        </td>
      </template>

      <template #repetition="{ item }">
        <td>
          {{ repetitionLabelMap[item.repetition] || item.repetition || "" }}
        </td>
      </template>

      <template #status="{ item }">
        <td>
          {{ item.scheduler_status || "" }}
        </td>
      </template>

      <template #action="{ item }">
        <td class="text-end">
          <Actions
            :actions="actions"
            :class="{
              'advance-reporting-loading': isLoading
            }"
            @click-action="handleTableAction($event, item)"
          >
          </Actions>
        </td>
      </template>
    </DataTable>
    <!-- TABLE -->

    <!-- PAGINATION -->
    <Pagination
      v-if="pagination.pages > 1 && !isLoading"
      :active-page="pagination.activePage"
      :pages="pagination.pages"
      class="cs-pagination mt-4"
      :dots="false"
      :double-arrows="true"
      size="lg"
      align="center"
      @update:active-page="setActivePage"
    ></Pagination>
    <!-- PAGINATION -->
  </div>
</template>

<script>
import useConfirmation from "@/composables/useConfirmation"
import advanceReportingConfigs from "@/configs/advanceReportingConfigs"
import advanceReportingConstants from "@/constants/advanceReportingConstants"
import NamesListPopUp from "@/v3components/AdvanceReporting/NamesListPopUp.vue"
import ErrorHandler from "@/v3components/VisitorErrorHandler.vue"
import Actions from "@/v3components/shared/DataTable/Actions.vue"
import DataTable from "@/v3components/shared/DataTable/DataTable.vue"
import Pagination from "@/v3components/shared/DataTable/Pagination.vue"
import Perpage from "@/v3components/shared/DataTable/Perpage.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import { debounce } from "lodash"
import moment from "moment-timezone"
import { computed, inject, onUnmounted, ref } from "vue"
import { useStore } from "vuex"

export default {
  name: "ScheduledReportsTable",
  components: {
    InputField,
    DataTable,
    Actions,
    Perpage,
    Pagination,
    ErrorHandler
  },
  setup() {
    const pageName = advanceReportingConfigs.pages.scheduledReportsPage().name

    const modal = inject("modal")
    const store = useStore()
    const { showConfirmation } = useConfirmation(modal)

    const isLoading = ref(false)
    const serverError = ref(null)

    const pageOptions = [
      { label: "25 / Page", value: 25 },
      { label: "50 / Page", value: 50 },
      { label: "100 / Page", value: 100 }
    ]

    const repetitionLabelMap = {
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly"
    }

    const fields = [
      { key: "school_name", label: "School", sorter: false, filter: false },
      { key: "format", label: "Format", sorter: false, filter: false },
      {
        key: "data_points",
        label: "Data points",
        sorter: false,
        filter: false
      },
      {
        key: "start_date",
        label: "Start date",
        sorter: false,
        filter: false
      },
      {
        key: "end_date",
        label: "End date",
        sorter: false,
        filter: false
      },
      {
        key: "repetition",
        label: "Repetition",
        sorter: false,
        filter: false
      },
      {
        key: "status",
        label: "Status",
        sorter: false,
        filter: false
      },
      {
        key: "action",
        label: "Action",
        _style: "width: 50px;",
        sorter: false,
        filter: false
      }
    ]

    const actions = [
      {
        label: "Delete",
        icon: "ri-delete-bin-line",
        class: "important",
        action: "delete",
        show: true
      },
      {
        label: "Edit",
        icon: "ri-pencil-line",
        class: "primary",
        action: "edit",
        show: false
      }
    ].filter((action) => action.show)

    const scheduledReports = computed(
      () => store.getters["advanceReportingStore/scheduledReports"]
    )

    const list = computed(() => scheduledReports?.value?.scheduledReports || [])

    const pagination = computed(() => scheduledReports?.value?.pagination || {})

    const paginationWithExtra = computed(() => {
      return {
        ...pagination.value,
        from:
          Number(pagination.value.activePage - 1) *
            Number(pagination.value.per_page.value) +
          1,
        to:
          Number(pagination.value.activePage - 1) *
            Number(pagination.value.per_page.value) +
          Number(pagination.value.per_page.value)
      }
    })

    const search = computed({
      get: () => scheduledReports.value?.search || "",
      set: debounce(async (value) => {
        store.commit("advanceReportingStore/setScheduledReportsSearch", value)
        try {
          serverError.value = null
          isLoading.value = true
          await store.dispatch("advanceReportingStore/fetchScheduledReports")
        } catch (error) {
          serverError.value = error
        } finally {
          isLoading.value = false
        }
      }, 500)
    })

    const shouldGoBackOnePage = computed(() => {
      const isActivePageGreaterThanOne =
        Number(pagination?.value?.activePage) > 1
      const isTotalPagesGreaterThanOne = Number(pagination?.value?.pages) > 1
      const isLastPage =
        Number(pagination?.value?.activePage) ===
        Number(pagination?.value?.pages)
      const onlyOneItemLeft = Number(list?.value?.length) === 1
      return (
        isActivePageGreaterThanOne &&
        isTotalPagesGreaterThanOne &&
        isLastPage &&
        onlyOneItemLeft
      )
    })

    const setPage = async (option) => {
      if (!option) return
      store.commit(
        "advanceReportingStore/setScheduledReportsPaginationPerPage",
        option
      )
      try {
        serverError.value = null
        isLoading.value = true
        await store.dispatch("advanceReportingStore/fetchScheduledReports")
      } catch (error) {
        serverError.value = error
      } finally {
        isLoading.value = false
      }
    }

    const setActivePage = async (page) => {
      if (!page) return
      store.commit(
        "advanceReportingStore/setScheduledReportsPaginationActivePage",
        page
      )
      try {
        serverError.value = null
        isLoading.value = true
        await store.dispatch("advanceReportingStore/fetchScheduledReports")
      } catch (error) {
        serverError.value = error
      } finally {
        isLoading.value = false
      }
    }

    const handleTableAction = async (event, item) => {
      if (event.action == "delete") {
        const body =
          "Are you sure you would like to delete this scheduled report? This action cannot be reversed."
        const confirmed = await showConfirmation({
          heading: "Please confirm",
          headingIcon: "ri ri-alert-line",
          headingColor: "red",
          headerPlacement: "start",
          body,
          confirmButtonText: "Confirm",
          cancelButtonText: "Cancel",
          cancelButtonSolid: false,
          buttonPlacement: "end"
        })

        if (!confirmed) {
          return
        }

        await deleteItem(item.id)
      }
    }

    const deleteItem = async (scheduledReportId) => {
      try {
        isLoading.value = true
        serverError.value = null
        await store.dispatch(
          "advanceReportingStore/deleteScheduledReport",
          scheduledReportId
        )
        if (shouldGoBackOnePage?.value) {
          store.commit(
            "advanceReportingStore/setScheduledReportsPaginationActivePage",
            Number(pagination?.value?.activePage) - 1
          )
        }
        await store.dispatch("advanceReportingStore/fetchScheduledReports")
      } catch (error) {
        serverError.value = error
      } finally {
        isLoading.value = false
      }
    }

    const getEmptyNamesResult = () => ({
      displayText: "",
      firstPart: "",
      plusPart: "",
      hasMultiple: false,
      allNames: []
    })

    const getSingleNameResult = (name) => ({
      displayText: name,
      firstPart: name,
      plusPart: "",
      hasMultiple: false,
      allNames: [name]
    })

    const getMultipleNamesResult = (names) => {
      const remainingCount = names.length - 1
      return {
        displayText: `${names[0]} +${remainingCount}`,
        firstPart: names[0],
        plusPart: `+${remainingCount}`,
        hasMultiple: true,
        allNames: names
      }
    }

    const mapBackendNameToDisplayTitle = (backendName) => {
      if (!backendName?.trim()) {
        return backendName || ""
      }

      const trimmedName = backendName.trim()
      const chartId = advanceReportingConstants.CHART_BE_NAME_VS_ID[trimmedName]

      if (!chartId) {
        return trimmedName
      }

      return advanceReportingConstants.CHART_ID_VS_TITLE[chartId] || trimmedName
    }

    const parseNamesString = (namesString) => {
      if (!namesString?.trim()) {
        return []
      }

      const trimmedString = namesString.trim()

      if (trimmedString === "All schools") {
        return [trimmedString]
      }

      return trimmedString
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean)
    }

    const formatNames = (namesString, isDataPoints = false) => {
      const namesArray = parseNamesString(namesString)

      if (namesArray.length === 0) {
        return getEmptyNamesResult()
      }

      const displayNames = isDataPoints
        ? namesArray.map(mapBackendNameToDisplayTitle)
        : namesArray

      if (displayNames.length === 1) {
        return getSingleNameResult(displayNames[0])
      }

      return getMultipleNamesResult(displayNames)
    }

    const getFormattedSchools = (item) => {
      return formatNames(item?.schools_names, false)
    }

    const getFormattedDataPoints = (item) => {
      return formatNames(item?.data_points_names, true)
    }

    const openNamesPopup = (title, names, rowItem) => {
      if (!names || names.length === 0) return
      modal.open(NamesListPopUp, {
        visible: true,
        size: "md",
        title: title,
        props: {
          title,
          names,
          rowItem
        }
      })
    }

    const formatDate = (dateString) => {
      if (!dateString) {
        return ""
      }

      const date = moment(dateString)
      if (!date.isValid()) {
        return dateString
      }

      return date.format("MMMM D, YYYY")
    }

    onUnmounted(() => {
      store.commit("advanceReportingStore/resetScheduledReportsState")
    })

    return {
      pageName,
      isLoading,
      serverError,
      search,
      list,
      pageOptions,
      pagination,
      paginationWithExtra,
      fields,
      actions,
      repetitionLabelMap,
      handleTableAction,
      setPage,
      setActivePage,
      getFormattedSchools,
      getFormattedDataPoints,
      formatDate,
      openNamesPopup
    }
  }
}
</script>
